import React, { useEffect, useRef, useState } from "react";

/**
 * Scroll-controlled hero video on desktop; simple autoplay loop on mobile/iOS.
 *
 * Why: programmatic scrubbing via video.currentTime is unreliable on iOS Safari
 * (often renders only a black frame) and any device that requires a user gesture
 * to play. So mobile devices get a plain autoplay+muted+loop fallback.
 */
export default function HeroVideo({ site, onTitleClick }) {
  const trackRef = useRef(null);
  const videoRef = useRef(null);
  const targetTimeRef = useRef(0);
  const rafRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [canScrub, setCanScrub] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  // Decide once on mount whether to use scroll-scrubbing or simple autoplay loop.
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iP(ad|hone|od)/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    const isMobileUA = /Mobi/i.test(ua);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const isSmall = window.innerWidth < 1024;
    const scrub = !(isTouch || isSmall || isIOS || isAndroid || isMobileUA);
    setCanScrub(scrub);
  }, []);

  // Mobile fallback: make sure the video is actually playing (iOS Safari often
  // needs a kick on first user interaction even when muted+playsInline).
  useEffect(() => {
    if (canScrub) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    const onInteract = () => { tryPlay(); window.removeEventListener("touchstart", onInteract); window.removeEventListener("click", onInteract); };
    window.addEventListener("touchstart", onInteract, { passive: true, once: true });
    window.addEventListener("click", onInteract, { once: true });
    return () => {
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("click", onInteract);
    };
  }, [canScrub, videoReady]);

  // Desktop scrubbing: pause once we know we can scrub, then drive currentTime via scroll.
  useEffect(() => {
    if (!canScrub) return;
    const v = videoRef.current;
    if (!v) return;
    v.loop = false;
    v.pause();
  }, [canScrub, videoReady]);

  useEffect(() => {
    if (!canScrub) return;
    const onScroll = () => {
      const track = trackRef.current;
      const v = videoRef.current;
      if (!track || !v || !v.duration || isNaN(v.duration)) return;
      const rect = track.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const passed = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? passed / total : 0;
      setProgress(p);
      targetTimeRef.current = Math.max(0, Math.min(v.duration - 0.05, v.duration * p));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [canScrub, videoReady]);

  useEffect(() => {
    if (!canScrub) return;
    const tick = () => {
      const v = videoRef.current;
      if (v && v.readyState >= 2) {
        const target = targetTimeRef.current;
        const current = v.currentTime;
        const diff = target - current;
        if (Math.abs(diff) > 0.012) {
          v.currentTime = current + diff * 0.18;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [canScrub, videoReady]);

  // Phase logic — desktop changes phase with scroll progress; mobile keeps phase 0
  // because the video plays as a continuous loop (no scrubbing).
  const phase = !canScrub ? 0 : (progress < 0.32 ? 0 : progress < 0.68 ? 1 : 2);

  const phases = [
    { kicker: site.hero_kicker || "", headline: site.hero_headline, sub: site.hero_sub },
    { kicker: site.hero_phase_2_kicker, headline: site.hero_phase_2_headline, sub: "Десять лет мы превращаем смыслы в кинематографичные сцены." },
    { kicker: site.hero_phase_3_kicker, headline: site.hero_phase_3_headline, sub: "Закрытый показ. Только для гостей продакшна." },
  ];
  const ph = phases[phase];

  return (
    <section
      data-testid="hero-video-section"
      ref={trackRef}
      className="bbf-hero-track relative"
      style={{ height: canScrub ? "360vh" : "100vh" }}
    >
      <div className="bbf-hero-sticky">
        <video
          data-testid="hero-video-element"
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          // eslint-disable-next-line react/no-unknown-property
          webkit-playsinline="true"
          preload="auto"
          poster="/media/hero-poster.jpg"
          onLoadedMetadata={() => setVideoReady(true)}
          onCanPlay={() => setVideoReady(true)}
        >
          <source src="/media/hero.mp4" type="video/mp4" />
          <source src="/media/hero.webm" type="video/webm" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/85 pointer-events-none" />
        <div className="absolute inset-0 bbf-vignette pointer-events-none" />

        {/* Headline — clickable; opens video iframe modal */}
        <div className="absolute inset-0 z-10 flex items-end sm:items-center pointer-events-none">
          <div className="px-5 sm:px-8 lg:px-12 pb-28 sm:pb-0 w-full">
            <div
              key={phase}
              data-testid={`hero-phase-${phase}`}
              className="max-w-[1400px] animate-[fadeIn_.7s_ease]"
            >
              <p className="overline mb-3 sm:mb-4 text-white/80 min-h-[1.2em]">{ph.kicker}</p>
              <button
                type="button"
                data-testid="hero-title-button"
                onClick={onTitleClick}
                className="pointer-events-auto group block text-left bbf-hero-title-btn"
              >
                <h1 className="font-display whitespace-pre-line text-[32px] leading-[1.05] sm:text-[48px] sm:leading-[1.1] lg:text-[clamp(40px,5vw,86px)] tracking-tight uppercase max-w-6xl">
                  {ph.headline}
              </h1>
                <span className="mt-4 sm:mt-5 inline-flex items-center gap-3 font-mono-jb text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-white/65 group-hover:text-white transition-colors">
                  <span className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center border border-white/40 group-hover:bg-white group-hover:text-black transition-colors">
                    ▶
                  </span>
                  Смотреть шоурил
                </span>
              </button>
              <p className="mt-5 sm:mt-6 max-w-2xl text-white/70 text-sm sm:text-base">
                {ph.sub}
              </p>
            </div>
          </div>
        </div>

        {/* Hint row */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-5 sm:px-8 lg:px-12 pb-6 sm:pb-8 flex items-center justify-end gap-4 flex-wrap pointer-events-none">
          <div className="hidden sm:flex items-center gap-3 font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/55">
            <span>{canScrub ? "SCROLL TO PLAY" : "AUTOPLAY"}</span>
            <span className="block w-12 h-px bg-white/30" />
            <span>↓</span>
          </div>
        </div>

        {/* Progress bar — only meaningful while scrubbing */}
        {canScrub && (
          <div className="absolute bottom-0 left-0 right-0 z-10 h-[2px] bg-white/8">
            <div
              data-testid="hero-progress-bar"
              className="h-full bbf-accent-gradient origin-left"
              style={{ transform: `scaleX(${progress})` }}
            />
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px);} to { opacity: 1; transform: none;} }
      `}</style>
    </section>
  );
}
